import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useAction } from '@agro-club/frontend-shared'
import { useTranslation } from 'react-i18next'
import { FormFields } from 'views/pages/DigitalAsset/DigitalAssetDetailsForm/types'
import { DigitalAsset, DigitalAssetEditDTO } from '../../../../modules/domain/digitalAsset/types'
import DigitalAssetActions from '../../../../modules/domain/digitalAsset/duck'
import { useForm } from 'antd/es/form/Form'
import { Card, ConfigProvider, Descriptions, DescriptionsProps, Flex, Form, Input } from 'antd'
import { isEqual } from 'lodash'
import { formatIsoDate } from '../../../../modules/utils/helpers'
import CSS from 'csstype'
import { assetNameCharacterLimit, descriptionCharacterLimit } from '../types'
import FooterActions from '../../../components/DigitalAssets/FooterAction'
import { UploadCompanyType } from 'types/entities'
import DigitalAssetCompanyTransfer from './DigitalAssetCompanyTransfer'

export type FormData = Omit<FormFields, 'file' | 'document_type' | 'document_name' | 'id'>

const { TextArea } = Input

type DigitalAssetDetailsFormProps = {
  digitalAsset: DigitalAsset
  onCancel(): void
}

const formContainerStyle: CSS.Properties = {
  display: 'flex',
  flex: 1,
  padding: '1.5em',
  background: '#FAFAFA',
}

const companyTransferCardStyles: CSS.Properties = {
  flex: 1,
  display: 'flex',
}

const cardBodyStyles: CSS.Properties = {
  display: 'flex',
  flex: 1,
}

const DigitalAssetDetailsForm: React.FC<DigitalAssetDetailsFormProps> = ({ onCancel, digitalAsset }) => {
  const { t } = useTranslation(['digitalAsset', 'common', 'validation'])
  const updateDigitalAssetAction = useAction(DigitalAssetActions.updateRequested)
  const [form] = useForm<FormData>()
  const [isFormValid, setIsFormValid] = useState<boolean>(true)
  const [isFormDirty, setIsFormDirty] = useState(false)
  const [validateForm, setValidateForm] = useState(false)
  const digitalAssetCompanyIds = digitalAsset.linked_companies?.map((d) => d.id) || []
  const initialValues = useRef<FormData>({
    asset_name: digitalAsset.asset_name,
    description: digitalAsset.description,
    selectedCompanies: digitalAssetCompanyIds,
  })
  const canLinkCompanies = digitalAsset && digitalAsset.uploaded_by_company_type === UploadCompanyType.Producer

  const updateServerCall = useCallback(
    (data: DigitalAssetEditDTO) => {
      updateDigitalAssetAction(digitalAsset.id, data)
    },
    [digitalAsset.id, updateDigitalAssetAction],
  )

  const submit = () => {
    const data = form.getFieldsValue()
    const selectedKeys = data.selectedCompanies?.map((key) => `${key}`) || []
    const args: DigitalAssetEditDTO = {
      id: digitalAsset.id,
      document_type: digitalAsset.document_type,
      document_name: digitalAsset.document_name,
      asset_name: data.asset_name,
      description: data.description,
      location: digitalAsset?.location,
      status: digitalAsset?.status,
      add_company_ids: selectedKeys.filter((key) => !digitalAssetCompanyIds.includes(key)),
      remove_company_ids: digitalAssetCompanyIds.filter((key) => !selectedKeys.includes(key)),
      created_at: digitalAsset.created_at,
      uploaded_by_company_type: digitalAsset.uploaded_by_company_type,
    }
    updateServerCall(args)
  }

  const staticContent: DescriptionsProps['items'] = [
    {
      key: '1',
      label: t('details.creationDate'),
      children: formatIsoDate(digitalAsset.created_at),
    },
    {
      key: '2',
      label: t('details.company'),
      children: digitalAsset?.company_name || '--',
    },
    {
      key: '3',
      label: t('details.createdBy'),
      children: digitalAsset?.user_name || '--',
    },
    {
      key: '4',
      label: t('details.fileType'),
      children: digitalAsset.document_type || '--',
    },
    {
      key: '5',
      label: t('details.fileSize'),
      children: digitalAsset.document_name || '--',
    },
  ]

  const checkFormDirtiness = () => {
    const currentValues = form.getFieldsValue()
    setIsFormDirty(() => !isEqual(currentValues, initialValues.current))
    setValidateForm(true)
  }

  const checkFormValid = useCallback(async () => {
    try {
      await form.validateFields()
      return true
    } catch (errorInfo) {
      if (errorInfo && (errorInfo as any).outOfDate) {
        return checkFormValid()
      }
      return false
    }
  }, [form])

  useEffect(() => {
    const validateForm = async () => {
      const valid = await checkFormValid()
      setIsFormValid(valid)
      setValidateForm(false)
    }
    validateForm()
  }, [checkFormValid, validateForm])

  const setSelectedCompanyIds = (selectedIds: string[]) => {
    form.setFieldValue('selectedCompanies', selectedIds)
    //by design, form setFieldValue does not trigger onValuesChange. It has to be triggered manually
    checkFormDirtiness()
  }

  return (
    <>
      <Form
        form={form}
        initialValues={initialValues.current}
        onValuesChange={checkFormDirtiness}
        style={formContainerStyle}
        layout="vertical"
      >
        <Flex vertical gap="large">
          <Card title={t('details.title')} variant="borderless">
            <Flex vertical gap="middle">
              <Descriptions column={6} size="small" layout="vertical" items={staticContent} />
              <Form.Item
                name="asset_name"
                layout="vertical"
                label={t('form.assetName')}
                rules={[
                  {
                    required: true,
                    message: t('form.rules.missingAssetName'),
                  },
                  {
                    max: assetNameCharacterLimit,
                    message: `${assetNameCharacterLimit} ${t('form.rules.assetNameLimitReached')}`,
                  },
                ]}
              >
                <TextArea
                  rows={1}
                  count={{
                    max: assetNameCharacterLimit,
                    show: true,
                  }}
                />
              </Form.Item>
              <Form.Item
                layout="vertical"
                name="description"
                label={t('form.documentDescription')}
                rules={[
                  {
                    required: true,
                    message: t('form.rules.missingDescription'),
                  },
                  {
                    max: descriptionCharacterLimit,
                    message: `${descriptionCharacterLimit} ${t('form.rules.descriptionLimitReached')}`,
                  },
                ]}
              >
                <TextArea
                  rows={2}
                  count={{
                    max: descriptionCharacterLimit,
                    show: true,
                  }}
                />
              </Form.Item>
            </Flex>
          </Card>
          {canLinkCompanies && (
            <ConfigProvider
              theme={{
                components: {
                  Card: {
                    bodyPadding: 16,
                  },
                  Form: {
                    itemMarginBottom: 0,
                  },
                },
              }}
            >
              <Card
                variant="borderless"
                style={companyTransferCardStyles}
                styles={{
                  body: cardBodyStyles,
                }}
              >
                <Form.Item name="selectedCompanies" layout="horizontal" noStyle>
                  <DigitalAssetCompanyTransfer
                    selectedCompanyIds={[...(digitalAsset.linked_companies?.map((company) => company.id) || [])]}
                    selectedCompaniesSetter={setSelectedCompanyIds}
                  />
                </Form.Item>
              </Card>
            </ConfigProvider>
          )}
        </Flex>
      </Form>
      <FooterActions
        saveButtonDisabled={!(isFormValid && isFormDirty)}
        saveButtonEvent={submit}
        cancelButtonEvent={onCancel}
      />
    </>
  )
}

export default DigitalAssetDetailsForm
